import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { PencilIcon, ArrowLeftIcon, TrashIcon, PlusIcon, MapPinIcon } from '@heroicons/react/20/solid'
import { createCrudApi } from '../../lib/api'
import { nullToUndefined } from '../../lib/utils'
import { AccountForm } from '../../components/forms/AccountForm'
import { AddressForm } from '../../components/forms/AddressForm'
import { Avatar } from '../../components/ui/Avatar'
import { useTranslation } from '../../hooks/useTranslation'
import { useUserLookup } from '../../hooks/useUsers'
import { useFls } from '../../hooks/useFls'
import { RecordSharePanel } from '../../components/sharing/RecordSharePanel'
import { AuditHistoryPanel } from '../../components/audit/AuditHistoryPanel'
import { EmailDropZone, EmailList, EmailDetail } from '../../components/emails'
import type { Account, AccountInput, Address, AddressInput } from '../../../shared/types'

const accountsApi = createCrudApi<Account, AccountInput>('accounts')
const addressesApi = createCrudApi<Address, AddressInput>('addresses')

export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [isAddingAddress, setIsAddingAddress] = useState(false)
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null)
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null)
  const { t } = useTranslation()
  const { getUser } = useUserLookup()
  const { canRead, canWrite } = useFls('accounts')

  const { data: account, isLoading } = useQuery({
    queryKey: ['accounts', id],
    queryFn: () => accountsApi.get(id!),
    enabled: !!id,
  })

  // Fetch addresses for this account
  const { data: addressesResponse } = useQuery({
    queryKey: ['addresses', 'byAccount', id],
    queryFn: () => addressesApi.list({ filters: { accountId: id! }, limit: 50 }),
    enabled: !!id,
  })

  const addresses = addressesResponse?.data || []
  const primaryAddress = addresses.find(a => a.type === 'primary')
  const billingAddress = addresses.find(a => a.type === 'billing')
  const shippingAddresses = addresses.filter(a => a.type === 'shipping')

  // Determine which address types are already taken
  const disabledTypes: ('primary' | 'billing' | 'shipping')[] = []
  if (primaryAddress && editingAddressId !== primaryAddress.id) disabledTypes.push('primary')
  if (billingAddress && editingAddressId !== billingAddress.id) disabledTypes.push('billing')

  const updateMutation = useMutation({
    mutationFn: (data: Partial<AccountInput>) => accountsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', id] })
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => accountsApi.delete(id!),
    onSuccess: () => {
      navigate('/accounts')
    },
  })

  // Address mutations
  const createAddressMutation = useMutation({
    mutationFn: (data: AddressInput) => addressesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses', 'byAccount', id] })
      setIsAddingAddress(false)
    },
  })

  const updateAddressMutation = useMutation({
    mutationFn: ({ addressId, data }: { addressId: string; data: Partial<AddressInput> }) =>
      addressesApi.update(addressId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses', 'byAccount', id] })
      setEditingAddressId(null)
    },
  })

  const deleteAddressMutation = useMutation({
    mutationFn: (addressId: string) => addressesApi.delete(addressId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses', 'byAccount', id] })
    },
  })

  if (isLoading) return <div>{t('common.loading')}</div>
  if (!account) return <div>{t('pages.accounts.notFound')}</div>

  return (
    <>
      {/* Secondary column (desktop sidebar) */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="mb-6">
          <Link
            to="/accounts"
            className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            {t('pages.accounts.backToList')}
          </Link>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{account.name}</h1>
          <p className="mt-1 text-sm text-gray-500">{account.type || t('nav.accounts')}</p>
        </div>

        <div className="space-y-3 mb-6">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
          >
            <PencilIcon className="h-4 w-4" />
            {isEditing ? t('common.cancel') : t('common.edit')}
          </button>
          <button
            onClick={() => {
              if (confirm(t('pages.accounts.confirmDelete'))) {
                deleteMutation.mutate()
              }
            }}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
          >
            <TrashIcon className="h-4 w-4" />
            {t('common.delete')}
          </button>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">{t('common.quickInfo')}</h2>
          <dl className="space-y-4">
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.type')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{account.type || '-'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.industry')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{account.industry || '-'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.phone')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{account.phone || '-'}</dd>
            </div>
            {account.website && (
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('fields.website')}</dt>
                <dd className="mt-1 text-sm">
                  <a
                    href={account.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-75 text-brand-500"
                  >
                    {account.website}
                  </a>
                </dd>
              </div>
            )}
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.owner')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {(() => {
                  const owner = getUser(account.ownerId)
                  if (!owner) return '-'
                  return (
                    <div className="flex items-center gap-x-2">
                      <Avatar firstName={owner.firstName} lastName={owner.lastName} size="xs" />
                      <span>{owner.firstName} {owner.lastName}</span>
                    </div>
                  )
                })()}
              </dd>
            </div>
          </dl>
        </div>

        {/* Record Sharing */}
        <RecordSharePanel entity="accounts" entityId={id!} ownerId={account.ownerId ?? undefined} />

        {/* Audit History */}
        <AuditHistoryPanel entity="accounts" entityId={id!} />
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] p-6">
        {/* Mobile header */}
        <div className="xl:hidden mb-6">
          <div className="mb-4">
            <Link
              to="/accounts"
              className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              {t('pages.accounts.backToList')}
            </Link>
          </div>
          <div className="flex gap-x-3">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="inline-flex items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
            >
              <PencilIcon className="h-4 w-4" />
              {isEditing ? t('common.cancel') : t('common.edit')}
            </button>
            <button
              onClick={() => {
                if (confirm(t('pages.accounts.confirmDelete'))) {
                  deleteMutation.mutate()
                }
              }}
              className="inline-flex items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
            >
              <TrashIcon className="h-4 w-4" />
              {t('common.delete')}
            </button>
          </div>
        </div>

        {/* Detail card */}
        <div className="overflow-hidden bg-white shadow sm:rounded-lg">
          <div className="px-4 py-6 sm:px-6">
            <div>
              <h3 className="text-base font-semibold text-gray-900">{account.name}</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">{t('pages.accounts.detailSubtitle')}</p>
            </div>
          </div>

          {isEditing ? (
            <div className="border-t border-gray-100 px-4 py-6 sm:px-6">
              <AccountForm
                defaultValues={nullToUndefined(account) as Partial<AccountInput>}
                onSubmit={(data) => updateMutation.mutate(data)}
                onCancel={() => setIsEditing(false)}
                loading={updateMutation.isPending}
                canWriteField={canWrite}
              />
            </div>
          ) : (
            <div className="border-t border-gray-100">
              <dl className="divide-y divide-gray-100">
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.name')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {account.name}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.type')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {account.type || '-'}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.industry')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {account.industry || '-'}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.phone')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {account.phone || '-'}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.website')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {account.website ? (
                      <a
                        href={account.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:opacity-75 text-brand-500"
                      >
                        {account.website}
                      </a>
                    ) : (
                      '-'
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </div>

        {/* Addresses Section */}
        <div className="mt-6 overflow-hidden bg-white shadow sm:rounded-lg">
          <div className="px-4 py-6 sm:px-6 flex justify-between items-center">
            <div>
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <MapPinIcon className="h-5 w-5 text-gray-400" />
                {t('fields.addresses')}
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                {addresses.length === 0 ? 'No addresses added yet' : `${addresses.length} address${addresses.length !== 1 ? 'es' : ''}`}
              </p>
            </div>
            {!isAddingAddress && !editingAddressId && (
              <button
                onClick={() => setIsAddingAddress(true)}
                className="inline-flex items-center gap-x-2 rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-500"
              >
                <PlusIcon className="h-4 w-4" />
                {t('fields.addAddress')}
              </button>
            )}
          </div>

          <div className="border-t border-gray-100">
            {/* Add new address form */}
            {isAddingAddress && (
              <div className="px-4 py-6 sm:px-6 bg-gray-50">
                <h4 className="text-sm font-medium text-gray-900 mb-4">New Address</h4>
                <AddressForm
                  accountId={id!}
                  onSubmit={(data) => createAddressMutation.mutate(data)}
                  onCancel={() => setIsAddingAddress(false)}
                  loading={createAddressMutation.isPending}
                  disabledTypes={disabledTypes}
                />
              </div>
            )}

            {/* Primary Address */}
            {primaryAddress && (
              <div className="px-4 py-6 sm:px-6 border-b border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <dt className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      {t('fields.primaryAddress')}
                      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                        {t('status.primary')}
                      </span>
                    </dt>
                    {editingAddressId === primaryAddress.id ? (
                      <div className="mt-2">
                        <AddressForm
                          defaultValues={nullToUndefined(primaryAddress)}
                          accountId={id!}
                          onSubmit={(data) => updateAddressMutation.mutate({ addressId: primaryAddress.id, data })}
                          onCancel={() => setEditingAddressId(null)}
                          loading={updateAddressMutation.isPending}
                          disabledTypes={disabledTypes}
                        />
                      </div>
                    ) : (
                      <dd className="mt-1 text-sm text-gray-700">
                        {primaryAddress.street}<br />
                        {primaryAddress.city}{primaryAddress.state ? `, ${primaryAddress.state}` : ''} {primaryAddress.postalCode}<br />
                        {primaryAddress.country}
                      </dd>
                    )}
                  </div>
                  {!editingAddressId && !isAddingAddress && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingAddressId(primaryAddress.id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Delete this address?')) {
                            deleteAddressMutation.mutate(primaryAddress.id)
                          }
                        }}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Billing Address */}
            {billingAddress && (
              <div className="px-4 py-6 sm:px-6 border-b border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <dt className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      {t('fields.billingAddress')}
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                        {t('status.billing')}
                      </span>
                    </dt>
                    {editingAddressId === billingAddress.id ? (
                      <div className="mt-2">
                        <AddressForm
                          defaultValues={nullToUndefined(billingAddress)}
                          accountId={id!}
                          onSubmit={(data) => updateAddressMutation.mutate({ addressId: billingAddress.id, data })}
                          onCancel={() => setEditingAddressId(null)}
                          loading={updateAddressMutation.isPending}
                          disabledTypes={disabledTypes}
                        />
                      </div>
                    ) : (
                      <dd className="mt-1 text-sm text-gray-700">
                        {billingAddress.street}<br />
                        {billingAddress.city}{billingAddress.state ? `, ${billingAddress.state}` : ''} {billingAddress.postalCode}<br />
                        {billingAddress.country}
                      </dd>
                    )}
                  </div>
                  {!editingAddressId && !isAddingAddress && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingAddressId(billingAddress.id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Delete this address?')) {
                            deleteAddressMutation.mutate(billingAddress.id)
                          }
                        }}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Billing defaults to Primary notice */}
            {!billingAddress && primaryAddress && (
              <div className="px-4 py-4 sm:px-6 border-b border-gray-100 bg-gray-50">
                <p className="text-sm text-gray-500 italic">
                  {t('fields.billingAddress')}: Using primary address (no separate billing address set)
                </p>
              </div>
            )}

            {/* Shipping Addresses */}
            {shippingAddresses.map((address) => (
              <div key={address.id} className="px-4 py-6 sm:px-6 border-b border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <dt className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      {t('fields.shippingAddress')}
                      <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                        {t('status.shipping')}
                      </span>
                    </dt>
                    {editingAddressId === address.id ? (
                      <div className="mt-2">
                        <AddressForm
                          defaultValues={nullToUndefined(address)}
                          accountId={id!}
                          onSubmit={(data) => updateAddressMutation.mutate({ addressId: address.id, data })}
                          onCancel={() => setEditingAddressId(null)}
                          loading={updateAddressMutation.isPending}
                          disabledTypes={disabledTypes}
                        />
                      </div>
                    ) : (
                      <dd className="mt-1 text-sm text-gray-700">
                        {address.street}<br />
                        {address.city}{address.state ? `, ${address.state}` : ''} {address.postalCode}<br />
                        {address.country}
                      </dd>
                    )}
                  </div>
                  {!editingAddressId && !isAddingAddress && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingAddressId(address.id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Delete this address?')) {
                            deleteAddressMutation.mutate(address.id)
                          }
                        }}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Empty state */}
            {addresses.length === 0 && !isAddingAddress && (
              <div className="px-4 py-12 sm:px-6 text-center">
                <MapPinIcon className="mx-auto h-12 w-12 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">No addresses yet</p>
                <button
                  onClick={() => setIsAddingAddress(true)}
                  className="mt-4 text-sm font-medium text-brand-600 hover:text-brand-500"
                >
                  Add the first address
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Emails Section */}
        <div className="mt-6 overflow-hidden bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900">{t('nav.emails') || 'Emails'}</h3>
            <p className="mt-1 text-sm text-gray-500">Emails linked to this account</p>
          </div>

          <div className="px-4 py-4 sm:px-6">
            <EmailDropZone
              targetType="account"
              targetId={id}
              compact
            />
          </div>

          <div className="px-4 pb-4 sm:px-6">
            <EmailList
              accountId={id}
              onEmailClick={setSelectedEmailId}
              limit={5}
            />
          </div>
        </div>
        </div>
      </div>

      {/* Email Detail Modal */}
      {selectedEmailId && (
        <EmailDetail
          emailId={selectedEmailId}
          onClose={() => setSelectedEmailId(null)}
        />
      )}
    </>
  )
}
